import api from './api';
import { ApiResponse, Tenant, PaginatedResponse } from '@invoiceflow/shared';

export interface ProvisionTenantData {
  name: string;
  code: string;
  adminName: string;
  adminEmail: string;
  adminPassword?: string;
  businessName?: string;
  taxId?: string;
  address?: string;
}

export const TenantService = {
  getTenants: async (params?: { page?: number; limit?: number; search?: string }) => {
    const response = await api.get<ApiResponse<PaginatedResponse<Tenant>>>('/tenants', { params });
    return response.data;
  },

  getTenant: async (id: string) => {
    const response = await api.get<ApiResponse<Tenant>>(`/tenants/${id}`);
    return response.data;
  },

  provisionTenant: async (data: ProvisionTenantData) => {
    const response = await api.post<ApiResponse<{ tenant: Tenant; adminUser: unknown }>>(
      '/tenants/provision',
      data
    );
    return response.data;
  },

  updateTenant: async (id: string, data: Partial<Tenant>) => {
    const response = await api.patch<ApiResponse<Tenant>>(`/tenants/${id}`, data);
    return response.data;
  },

  updateSettings: async (
    id: string,
    settings: Partial<Tenant['settings']> | Record<string, unknown>
  ) => {
    const response = await api.patch<ApiResponse<Tenant>>(`/tenants/${id}/settings`, settings);
    return response.data;
  },

  updateChannelIdentities: async (id: string, identities: Partial<Tenant['channelIdentities']>) => {
    const response = await api.patch<ApiResponse<Tenant>>(
      `/tenants/${id}/channel-identities`,
      identities
    );
    return response.data;
  },

  toggleFeature: async (id: string, feature: string, enabled: boolean) => {
    const response = await api.patch<ApiResponse<Tenant>>(`/tenants/${id}/features/${feature}`, {
      enabled,
    });
    return response.data;
  },

  getUsers: async (tenantId: string) => {
    const response = await api.get<ApiResponse<any[]>>(`/tenants/${tenantId}/users`);
    return response.data;
  },

  createUser: async (tenantId: string, data: any) => {
    const response = await api.post<ApiResponse<any>>(`/tenants/${tenantId}/users`, data);
    return response.data;
  },

  updateUser: async (tenantId: string, userId: string, data: any) => {
    const response = await api.patch<ApiResponse<any>>(
      `/tenants/${tenantId}/users/${userId}`,
      data
    );
    return response.data;
  },

  resetUserPassword: async (tenantId: string, userId: string, password: string) => {
    const response = await api.post<ApiResponse<any>>(
      `/tenants/${tenantId}/users/${userId}/reset-password`,
      { password }
    );
    return response.data;
  },

  deleteUser: async (tenantId: string, userId: string) => {
    const response = await api.delete<ApiResponse<any>>(`/tenants/${tenantId}/users/${userId}`);
    return response.data;
  },
};
